import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Spin,
  Collapse,
  Switch,
  Tag,
  Icon,
  Modal,
  notification,
  Popover,
  Tooltip,
  Select,
  Row,
  Col,
  Popconfirm,
  Input,
  message,
  Typography,
} from "antd";
import api from "../../../../../services/api";
import { useFreire } from "../../../../../utils/freireContext";
import { formatters } from "../../../../../utils/formatters";
import { useOnUpdate } from "../../../../../hooks";
import * as texts from "../../../../../pages/Users/locales";
import "./styles.css";
import InfoRow from "../../../../../components/InfoRow";
import moment from "moment";
import { Show, useAccess } from "../../../../../contexts/PermissionContext";
import { Copy } from "../../../../../components/Copy";
import { LANGUAGES } from "../../../../../config/languages";
import { AddressTooltip } from "../../../../../components/AddressTooltip";
import { normalizeUserStatus } from "../../../../../constants/user";
import { useWhitelabels } from "../../../../../components/WhitelabelThemesProvider";
import { ChargingMode } from "../../../../../constants/charging-settings";
const { Panel } = Collapse;
const { Text } = Typography;

const PAYMENT_METHOD_BRANDS = {
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  MASTERCARD_DEBIT: "Mastercard",
  MASTER: "Mastercard",
  DINERS: "Diners Club",
  DINERS_CLUB: "Diners Club",
  DISCOVER: "Discover",
  AMEX: "Amex",
  AMERICAN_EXPRESS: "Amex",
  HIPERCARD: "Hipercard",
  ELO: "Elo",
  JCB: "JCB",
  MAESTRO: "Maestro",
  CABAL: "Cabal",
  AURA: "Aura",
  UNIONPAY: "UnionPay",
  ELECTRON: "Electron",
  PIX: "Pix",
  WALLET: "wallet",
};

const BRAZIL_COUNTRY_CODES = new Set(["BR", "BRA", "BRAZIL", "BRASIL"]);

const logHandledError = (context, error) => {
  console.error(`[UserDetailsAndCharges/Details] ${context}`, error);
};

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
};

const normalizeCode = (value) =>
  typeof value === "string" ? value.trim().toUpperCase() : null;

const getBooleanValue = (...values) => {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }
  return null;
};

const sanitizeDocument = (document) =>
  typeof document === "string" ? document.replaceAll(/\D/g, "") : "";

const getBooleanLabel = (freire, value) =>
  freire(value ? texts.YES : texts.NO);

const getPaymentMethodBrand = (paymentMethod) => {
  if (!paymentMethod) {
    return "";
  }

  const brandSources = [
    paymentMethod.brand,
    paymentMethod.type,
    paymentMethod.method,
    paymentMethod.paymentMethodType,
  ];

  const brand = brandSources.find(
    (value) => typeof value === "string" && value.trim().length > 0
  );

  return brand ? brand.trim() : "";
};

const getPaymentMethodBrandLabel = ({ freire, paymentMethod }) => {
  const brand = getPaymentMethodBrand(paymentMethod);
  const normalizedBrand = normalizeCode(brand);
  const mappedBrand = normalizedBrand
    ? PAYMENT_METHOD_BRANDS[normalizedBrand]
    : null;

  if (mappedBrand === "wallet") {
    return freire(texts.WALLET);
  }

  return mappedBrand ?? getFirstNonEmptyString(brand) ?? freire(texts.CREDIT);
};

const formatPaymentMethod = ({ freire, paymentMethod }) =>
  `${getPaymentMethodBrandLabel({ freire, paymentMethod })}${
    paymentMethod.suffix === undefined ? "" : ` ${paymentMethod.suffix}`
  }${
    typeof paymentMethod.balance === "number"
      ? ` (${formatters.currency(
          freire.userLanguage,
          paymentMethod.currency
        )(paymentMethod.balance / 100)})`
      : ""
  }`;

const isWallet = (paymentMethod) =>
  getPaymentMethodBrand(paymentMethod).toLowerCase() === "wallet";

const renderTagInvalid = ({ freire, status, female = false }) => {
  if (!status) {
    return null;
  }

  return (
    <Tag color="red" className="paymentStatusTag" style={{ paddingLeft: 2 }}>
      <Icon type="close" style={{ color: "red", marginRight: 3 }} />
      {freire(female ? texts.INVALID_FEMALE : texts.INVALID)}
    </Tag>
  );
};

const renderTagWarningInvalid = ({ freire, status, female = false }) => {
  if (!status) {
    return null;
  }

  return (
    <Tag
      color="orange"
      className="paymentStatusTag"
      style={{ paddingLeft: 6 }}
    >
      <Icon
        type="exclamation-circle"
        style={{ color: "#eb7c05", marginRight: 5 }}
      />
      {freire(female ? texts.INVALID_FEMALE : texts.INVALID)}
    </Tag>
  );
};

const shouldShowDocumentValidation = (userDetails) => {
  if (!userDetails?.birthdate || userDetails?.invalids?.personal?.birthdate) {
    return false;
  }

  const rawDocument = sanitizeDocument(userDetails?.document);
  if (rawDocument.length !== 11) {
    return false;
  }

  const country = normalizeCode(
    getFirstNonEmptyString(
      userDetails?.country,
      userDetails?.address?.country,
      userDetails?.address?.countryCode,
      userDetails?.address?.country_code
    )
  );

  return country
    ? BRAZIL_COUNTRY_CODES.has(country)
    : userDetails?.currency === "BRL";
};

const getDocumentValidationUrl = (userDetails) => {
  if (!shouldShowDocumentValidation(userDetails)) {
    return null;
  }

  const params = new URLSearchParams({
    CPF: sanitizeDocument(userDetails.document),
    NASCIMENTO: moment.utc(userDetails.birthdate).format("DD/MM/YYYY"),
  });

  return `https://servicos.receita.fazenda.gov.br/servicos/cpf/consultasituacao/ConsultaPublica.asp?${params.toString()}`;
};

const renderDocumentTag = ({ freire, userDetails }) => {
  if (userDetails.invalids?.personal?.document) {
    return renderTagInvalid({ freire, status: true });
  }

  const validationUrl = getDocumentValidationUrl(userDetails);
  if (!validationUrl) {
    return null;
  }

  return (
    <Tooltip placement="topRight" title={freire(texts.VALIDATE_DOCUMENT)}>
      <Button
        shape="circle"
        size="small"
        ghost
        type="primary"
        style={{ marginLeft: 16 }}
        rel="noopener noreferrer"
        target="_blank"
        href={validationUrl}
      >
        <Icon type="idcard" />
      </Button>
    </Tooltip>
  );
};

const getPaymentMethodBalance = (paymentMethod) =>
  typeof paymentMethod?.balance === "number" ? paymentMethod.balance : null;

const getPaymentMethodCurrency = (paymentMethod) =>
  getFirstNonEmptyString(
    paymentMethod?.currency,
    paymentMethod?.card?.currency,
    paymentMethod?.metadata?.currency,
    paymentMethod?.details?.currency
  );

const getPaymentMethodCountry = (paymentMethod) =>
  getFirstNonEmptyString(
    paymentMethod?.country,
    paymentMethod?.billingAddress?.country,
    paymentMethod?.billing_address?.country,
    paymentMethod?.address?.country,
    paymentMethod?.holder?.address?.country
  );

const getWalletCurrency = (walletPaymentMethod) =>
  getFirstNonEmptyString(walletPaymentMethod?.currency);

const getWalletCountry = (walletPaymentMethod) =>
  getFirstNonEmptyString(
    walletPaymentMethod?.country,
    walletPaymentMethod?.address?.country
  );

const isPrimaryCompatibleWithWallet = (
  primaryPaymentMethod,
  walletPaymentMethod
) => {
  if (!primaryPaymentMethod || !walletPaymentMethod) {
    return false;
  }

  const walletCurrency = normalizeCode(getWalletCurrency(walletPaymentMethod));
  const walletCountry = normalizeCode(getWalletCountry(walletPaymentMethod));
  const primaryCurrency = normalizeCode(
    getPaymentMethodCurrency(primaryPaymentMethod)
  );
  const primaryCountry = normalizeCode(
    getPaymentMethodCountry(primaryPaymentMethod)
  );

  const hasCurrencyMismatch =
    walletCurrency && primaryCurrency && walletCurrency !== primaryCurrency;
  const hasCountryMismatch =
    walletCountry && primaryCountry && walletCountry !== primaryCountry;

  return !(hasCurrencyMismatch || hasCountryMismatch);
};

const getWalletIdentifier = (paymentMethod) => {
  const rawId =
    paymentMethod?._id ??
    paymentMethod?.walletId ??
    paymentMethod?.paymentMethodId ??
    paymentMethod?.id ??
    null;

  if (!rawId) {
    return null;
  }

  return typeof rawId === "string" ? rawId : String(rawId);
};

const getPaymentMethodExpiration = (paymentMethod) => {
  const month = Number(
    paymentMethod?.expMonth ??
      paymentMethod?.exp_month ??
      paymentMethod?.expiryMonth ??
      paymentMethod?.expirationMonth
  );
  const year = Number(
    paymentMethod?.expYear ??
      paymentMethod?.exp_year ??
      paymentMethod?.expiryYear ??
      paymentMethod?.expirationYear
  );
  const expiresAt =
    paymentMethod?.expiresAt ??
    paymentMethod?.expires_at ??
    paymentMethod?.expirationDate ??
    paymentMethod?.expiryDate;

  return {
    month: Number.isInteger(month) && month > 0 ? month : null,
    year: Number.isInteger(year) && year > 0 ? year : null,
    expiresAt,
  };
};

const isPaymentMethodExpired = (paymentMethod) => {
  if (!paymentMethod || isWallet(paymentMethod)) {
    return false;
  }

  const { month, year, expiresAt } = getPaymentMethodExpiration(paymentMethod);

  if (month && year) {
    const normalizedYear = year < 100 ? 2000 + year : year;
    const expiration = moment({
      year: normalizedYear,
      month: month - 1,
    }).endOf("month");

    return expiration.isBefore(moment(), "day");
  }

  if (expiresAt) {
    return moment(expiresAt).endOf("day").isBefore(moment(), "day");
  }

  return false;
};

const renderExpiredPaymentMethodTag = ({ freire, paymentMethod }) =>
  isPaymentMethodExpired(paymentMethod) ? (
    <Tag color="red" className="paymentStatusTag" style={{ marginLeft: 16 }}>
      {freire(texts.EXPIRED)}
    </Tag>
  ) : null;

const renderPaymentMethodsTag = ({
  freire,
  primaryPaymentMethod,
  userDetails,
}) => {
  if (!userDetails?.paymentMethods?.[0]) {
    return renderTagWarningInvalid({ freire, status: true, female: true });
  }

  const tag = (
    <Tag className="paymentStatusTag">{userDetails.paymentMethods.length}</Tag>
  );

  const paymentMethodsWalletFirst = [
    ...userDetails.paymentMethods.filter(isWallet),
    ...userDetails.paymentMethods.filter(
      (paymentMethod) => !isWallet(paymentMethod)
    ),
  ];

  return paymentMethodsWalletFirst.length > 1 ? (
    <Popover
      content={paymentMethodsWalletFirst.map((paymentMethod) => {
        const content = (
          <Copy>{formatPaymentMethod({ freire, paymentMethod })}</Copy>
        );

        return (
          <div key={paymentMethod._id}>
            {paymentMethod._id === primaryPaymentMethod?._id ? (
              <strong>{content}</strong>
            ) : (
              content
            )}
          </div>
        );
      })}
    >
      {tag}
    </Popover>
  ) : (
    tag
  );
};

const getEmailVerificationValue = (userDetails) =>
  getBooleanValue(
    userDetails?.emailVerified,
    userDetails?.verifiedEmail,
    userDetails?.email_verified,
    userDetails?.firebaseUser?.emailVerified,
    userDetails?.firebase?.emailVerified
  );

const UnifyAccountsRow = ({ freire, hasPermission, primaryUserId }) => {
  const [isUnifyVisible, setIsUnifyVisible] = useState(false);
  const [secondaryUserId, setSecondaryUserId] = useState("");
  const [unifyLoading, setUnifyLoading] = useState(false);

  const handleUnifyAccounts = async () => {
    setUnifyLoading(true);
    try {
      await api.post("admin/users/unify-accounts", {
        primaryUserId,
        secondaryUserId,
      });
      notification.success({ message: freire(texts.UNIFY_ACCOUNTS_SUCCESS) });
      setIsUnifyVisible(false);
      setSecondaryUserId("");
    } catch (error) {
      notification.error({
        message: freire(texts.UNIFY_ACCOUNTS_ERROR),
        description: error?.response?.data?.error,
      });
    } finally {
      setUnifyLoading(false);
    }
  };

  if (!hasPermission("unify-duplicate-accounts")) return null;

  return (
    <>
      <Show when={"developer"}>
        <InfoRow
          labelSpan={7}
          valueSpan={17}
          label="userId"
          value={primaryUserId}
          bold={false}
          allowCopy
          extraItems={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 16 }}>
              <Tooltip placement="topRight" title={freire(texts.UNIFY_ACCOUNTS)}>
                <Button
                  shape="circle"
                  size="small"
                  ghost
                  type="danger"
                  onClick={() => setIsUnifyVisible(!isUnifyVisible)}
                >
                  <Icon type="swap" />
                </Button>
              </Tooltip>
              <Tooltip placement="topRight" title={freire(texts.UNIFY_ACCOUNTS_INFO)}>
                <Icon type="info-circle" style={{ color: "#aaa", cursor: "pointer" }} />
              </Tooltip>
            </span>
          }
        />
      </Show>
      {isUnifyVisible && (
        <Row className="statusChanger">
          <Col span={7}>{freire(texts.UNIFY_ACCOUNTS)}:</Col>
          <Col span={17}>
            <Row>
              <Col span={24}>
                <Input
                  placeholder={freire(texts.UNIFY_ACCOUNTS_SECONDARY_ID_PLACEHOLDER)}
                  value={secondaryUserId}
                  onChange={(e) => setSecondaryUserId(e.target.value)}
                />
              </Col>
            </Row>
            <Row type="flex" justify="end" style={{ marginTop: 10 }}>
              <Popconfirm
                title={freire(texts.UNIFY_ACCOUNTS_CONFIRM)}
                onConfirm={handleUnifyAccounts}
                disabled={!secondaryUserId.trim()}
                okType="danger"
              >
                <Button
                  type="danger"
                  loading={unifyLoading}
                  disabled={!secondaryUserId.trim()}
                >
                  {freire(texts.UNIFY_ACCOUNTS)}
                  <Icon type="swap" />
                </Button>
              </Popconfirm>
            </Row>
          </Col>
        </Row>
      )}
    </>
  );
};

const PasswordResetTokenRow = ({ freire, email }) => {
  const [loading, setLoading] = useState(false);

  const handleGenerateToken = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/passwordResetToken", { email });
      Modal.success({
        title: freire(texts.GENERATE_PASSWORD_RESET_TOKEN_SUCCESS),
        content: (
          <div>
            <div>
              {freire(texts.GENERATE_PASSWORD_RESET_TOKEN_INSTRUCTIONS)}
            </div>
            <div style={{ marginTop: 12, fontSize: 18, textAlign: "center" }}>
              <strong>{data.code}</strong>
            </div>
          </div>
        ),
      });
    } catch (error) {
      notification.error({
        message: freire(texts.GENERATE_PASSWORD_RESET_TOKEN_ERROR),
        description: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Show when={"generate-password-reset-token"}>
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.PASSWORD)}:`}
        value="*******"
        bold={false}
        extraItems={
          <Tooltip
            placement="topRight"
            title={freire(texts.GENERATE_PASSWORD_RESET_TOKEN)}
          >
            <Button
              shape="circle"
              size="small"
              ghost
              type="primary"
              loading={loading}
              onClick={handleGenerateToken}
              style={{ marginLeft: 16 }}
            >
              <Icon type="unlock" />
            </Button>
          </Tooltip>
        }
      />
    </Show>
  );
};

const DetailsPanelContent = ({
  allStatuses,
  antifraudCheck,
  canForceStatus,
  forceStatus,
  freire,
  getWhitelabelByAlias,
  hasAnyStatusPermission,
  hasPermission,
  isLimitedChargingMode,
  isStatusChangeModalVisible,
  loadStatusOptions,
  loading,
  loadingStatusOptions,
  loadingToggle,
  optionsError,
  primaryPaymentMethod,
  renderIsPersonFraudster,
  renderNegativeBalanceTag,
  saveNewUserStatus,
  setForceStatus,
  setIsStatusChangeModalVisible,
  setUserNewStatus,
  setUserNewStatusReason,
  statusOptions,
  toggleAntifraudeCheck,
  userDetails,
  userNewStatus,
  userNewStatusReason,
  userTotalPendingBalance,
}) => {
  if (loading) {
    return (
      <Spin
        style={{ display: "block", marginTop: 25, marginBottom: 25 }}
        tip={freire(texts.LOADING_USER_DETAILS)}
      />
    );
  }

  if (!userDetails) {
    return null;
  }

  const emailVerificationValue = getEmailVerificationValue(userDetails);
  const documentRaw = sanitizeDocument(userDetails?.document);
  const paymentMethodExtraItems = (
    <>
      {renderPaymentMethodsTag({
        freire,
        primaryPaymentMethod,
        userDetails,
      })}
      {renderExpiredPaymentMethodTag({
        freire,
        paymentMethod: primaryPaymentMethod,
      })}
    </>
  );

  const saveStatusButton = (
    <Button
      loading={loadingToggle}
      disabled={!userNewStatus}
      onClick={forceStatus ? undefined : () => saveNewUserStatus()}
    >
      {freire(texts.SAVE_NEW_STATUS)}
      <Icon type="save" theme="twoTone" />
    </Button>
  );

  const statusOptionsAlert = optionsError ? (
    <Alert
      type="error"
      message={freire(texts.STATUS_OPTIONS_LOAD_ERROR)}
      description={
        <Button
          size="small"
          icon="reload"
          onClick={() => loadStatusOptions()}
        >
          {freire(texts.RETRY_LOADING_STATUS_OPTIONS)}
        </Button>
      }
      style={{ marginBottom: 8 }}
    />
  ) : statusOptions.length === 0 && !forceStatus && !loadingStatusOptions ? (
    <Alert
      type="info"
      message={freire(texts.NO_STATUS_TRANSITION_AVAILABLE)}
      style={{ marginBottom: 8 }}
    />
  ) : null;

  return (
    <div style={{ marginTop: 5, paddingLeft: 24, paddingRight: 24 }}>
      <UnifyAccountsRow
        freire={freire}
        hasPermission={hasPermission}
        primaryUserId={userDetails._id}
      />
      <Show when={"developer"}>
        <InfoRow
          labelSpan={7}
          valueSpan={17}
          label="userID"
          value={userDetails.userID}
          bold={false}
          allowCopy
        />
      </Show>
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.APPS)}:`}
        value={(userDetails?.appsLogged ?? []).map((appName) => {
          const whitelabel = getWhitelabelByAlias(appName, true);

          if (!whitelabel) return null;

          return (
            <Popover
              key={appName}
              content={
                <div>
                  {whitelabel.name}
                  <br />
                  <br />
                  <strong>{freire(texts.NOTIFICATIONS_ENABLED)}:</strong>{" "}
                  {getBooleanLabel(freire, Boolean(userDetails?.fcmToken?.[appName]))}
                </div>
              }
            >
              <img
                src={whitelabel.favicon}
                width={18}
                style={{ marginRight: 8, marginBottom: 2 }}
                alt={whitelabel.name}
              />
            </Popover>
          );
        })}
        bold={false}
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.LANGUAGE)}:`}
        value={
          LANGUAGES[userDetails?.language]
            ? freire(LANGUAGES[userDetails.language].text)
            : null
        }
        bold={false}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.NAME)}:`}
        value={userDetails?.displayName}
        bold={false}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.FULL_NAME)}:`}
        value={userDetails?.fullName}
        extraItems={renderTagInvalid({
          freire,
          status: userDetails.invalids?.personal?.fullName,
        })}
        bold={false}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.EMAIL)}:`}
        value={userDetails?.email}
        extraItems={
          emailVerificationValue === null ? null : (
            <Tag
              color={emailVerificationValue ? "green" : "orange"}
              className="paymentStatusTag"
              style={{ marginLeft: 16 }}
            >
              {freire(
                emailVerificationValue ? texts.VERIFIED : texts.NOT_VERIFIED
              )}
            </Tag>
          )
        }
        tooltip={
          emailVerificationValue === null ? null : (
            <div>
              <strong>{freire(texts.EMAIL_VERIFIED)}:</strong>{" "}
              {getBooleanLabel(freire, emailVerificationValue)}
            </div>
          )
        }
        bold={false}
        allowCopy
      />
      <PasswordResetTokenRow freire={freire} email={userDetails?.email} />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.DOCUMENT)}:`}
        value={userDetails?.document}
        extraItems={renderDocumentTag({ freire, userDetails })}
        bold={false}
        {...(documentRaw.length > 0 && {
          tooltip: (
            <div>
              <strong>{freire(texts.RAW)}:</strong> <Copy>{documentRaw}</Copy>
            </div>
          ),
          tooltipWhen: "developer",
        })}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.BIRTHDATE)}:`}
        value={
          userDetails?.birthdate
            ? moment.utc(userDetails.birthdate).format("L")
            : userDetails?.birthdate
        }
        extraItems={renderTagInvalid({
          freire,
          status: userDetails.invalids?.personal?.birthdate,
          female: true,
        })}
        bold={false}
        {...((userDetails?.birthdate ?? "").length > 0 && {
          tooltip: (
            <div>
              <div>
                <strong>{freire(texts.AGE)}:</strong>{" "}
                <Copy>
                  {freire(
                    texts.YEARS_OLD(
                      moment().diff(userDetails.birthdate, "years")
                    )
                  )}
                </Copy>
              </div>
              <Show when={"developer"}>
                <br />
                <div>
                  <strong>{freire(texts.RAW)}:</strong>{" "}
                  <Copy>{userDetails.birthdate}</Copy>
                </div>
              </Show>
            </div>
          ),
        })}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.PHONE)}:`}
        value={formatters.phone(userDetails?.phone)}
        extraItems={renderTagInvalid({
          freire,
          status: userDetails.invalids?.personal?.phone,
        })}
        bold={false}
        {...((userDetails?.phone ?? "").length > 0 && {
          tooltip: (
            <div>
              <strong>{freire(texts.RAW)}:</strong>{" "}
              <Copy>{userDetails.phone}</Copy>
            </div>
          ),
          tooltipWhen: "developer",
        })}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.ADDRESS)}:`}
        value={formatters.formatAddress(userDetails?.address)}
        extraItems={renderTagInvalid({
          freire,
          status: userDetails.invalids?.address?.logradouro,
        })}
        bold={false}
        {...(userDetails?.address && {
          tooltip: <AddressTooltip address={userDetails.address} />,
        })}
        allowCopy
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.PAYMENT_METHOD)}:`}
        value={
          userDetails?.paymentMethods.length > 0 && primaryPaymentMethod ? (
            <Copy>
              {formatPaymentMethod({
                freire,
                paymentMethod: primaryPaymentMethod,
              })}
            </Copy>
          ) : null
        }
        extraItems={paymentMethodExtraItems}
        bold={false}
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.BALANCE)}:`}
        value={
          userTotalPendingBalance === 0 ? null : (
            <Copy
              style={{
                color: userTotalPendingBalance < 0 ? "#f40000" : "#007000",
              }}
            >
              {formatters.currency(freire.userLanguage, userDetails.currency)(
                Math.abs(userTotalPendingBalance) / 100
              )}
            </Copy>
          )
        }
        bold={false}
        extraItems={renderNegativeBalanceTag()}
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.IS_PERSON_FRAUDSTER)}:`}
        value={renderIsPersonFraudster()}
        bold={false}
      />
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.USER_STATUS)}:`}
        value={normalizeUserStatus(userDetails?.status)}
        extraItems={
          hasAnyStatusPermission ? (
            <Tooltip
              placement="topRight"
              title={freire(texts.CHANGE_USER_STATUS)}
            >
              <Button
                shape="circle"
                size="small"
                ghost
                type="primary"
                style={{ marginLeft: 16 }}
                rel="noopener noreferrer"
                onClick={() => {
                  const next = !isStatusChangeModalVisible;
                  setIsStatusChangeModalVisible(next);
                  if (next) {
                    loadStatusOptions();
                  } else {
                    setForceStatus(false);
                    setUserNewStatus(null);
                    setUserNewStatusReason(null);
                  }
                }}
              >
                <Icon type="edit" />
              </Button>
            </Tooltip>
          ) : null
        }
        bold={false}
        tooltip={
          <div>
            <strong>{freire(texts.USER_STATUS_REASON)}:</strong>{" "}
            {userDetails?.status_reason}
            <br /> <br />
            {Object.keys(freire(texts.USER_STATUS_DESCRIPTION)).map((key) => (
              <div key={key}>
                <strong>{key}:</strong>{" "}
                {freire(texts.USER_STATUS_DESCRIPTION)[key]}
              </div>
            ))}
          </div>
        }
        allowCopy
      />
      {isStatusChangeModalVisible && (
        <Row className="statusChanger">
          <Col span={7}>{freire(texts.CHANGE_USER_STATUS)}:</Col>
          <Col span={17}>
            {statusOptionsAlert}
            <Row>
              <Col span={12}>
                <Select
                  value={userNewStatus || undefined}
                  loading={loadingStatusOptions}
                  onChange={(value) => setUserNewStatus(value)}
                  notFoundContent={freire(texts.NO_STATUS_OPTION_TO_PICK)}
                  style={{ width: "100%", paddingRight: "8px" }}
                >
                  {(forceStatus ? allStatuses : statusOptions).map((key) => (
                    <Select.Option key={key} value={key}>
                      {freire(texts.USER_STATUS_DESCRIPTION)[key] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <Input
                  value={userNewStatusReason || ""}
                  placeholder={freire(texts.STATUS_REASON_PLACEHOLDER)}
                  onChange={(e) => setUserNewStatusReason(e.target.value)}
                />
              </Col>
            </Row>
            {canForceStatus && (
              <Row type="flex" justify="start" style={{ marginTop: 8 }}>
                <Checkbox
                  checked={forceStatus}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForceStatus(checked);
                    if (!checked) setUserNewStatus(null);
                  }}
                >
                  <span style={{ color: "#f5222d" }}>
                    {freire(texts.FORCE_STATUS_LABEL)}
                  </span>
                </Checkbox>
              </Row>
            )}
            <Row type="flex" justify="end" style={{ marginTop: 10 }}>
              {forceStatus ? (
                <Popconfirm
                  title={freire(texts.FORCE_STATUS_CONFIRM)}
                  okType="danger"
                  okText={freire(texts.YES)}
                  cancelText={freire(texts.NO)}
                  onConfirm={() => saveNewUserStatus()}
                >
                  {saveStatusButton}
                </Popconfirm>
              ) : (
                saveStatusButton
              )}
            </Row>
          </Col>
        </Row>
      )}
      <InfoRow
        labelSpan={7}
        valueSpan={17}
        label={`${freire(texts.CHARGING_MODE)}:`}
        value={userDetails?.charging_settings?.charging_mode}
        bold={false}
        tooltip={
          <div>
            {Object.keys(freire(texts.USER_CHARGING_MODE_DESCRIPTION)).map(
              (key) => (
                <div key={key}>
                  <strong>{key}:</strong>{" "}
                  {freire(texts.USER_CHARGING_MODE_DESCRIPTION)[key]}
                </div>
              )
            )}
          </div>
        }
        allowCopy
      />
      {isLimitedChargingMode &&
        userDetails?.charging_settings?.charging_limit > 0 && (
          <InfoRow
            labelSpan={7}
            valueSpan={17}
            label={`${freire(texts.CHARGING_LIMIT)}:`}
            value={formatters.currency(
              freire.userLanguage,
              userDetails.currency
            )(userDetails?.charging_settings?.charging_limit / 100)}
            bold={false}
            allowCopy
          />
        )}
      <Show when={"edit-user-antifraud-check"}>
        <div className="containerSwitch">
          <Switch
            checkedChildren={freire(texts.YES)}
            unCheckedChildren={freire(texts.NO)}
            checked={antifraudCheck}
            onChange={toggleAntifraudeCheck}
            loading={loadingToggle}
          />
          <span style={{ marginLeft: 10 }}>
            {freire(texts.USER_ANTIFRAUD_CHECK)}
          </span>
        </div>
      </Show>
    </div>
  );
};

const Details = ({ userID, opened = false }) => {
  const { freire } = useFreire();
  const { getWhitelabelByAlias } = useWhitelabels();

  const [loading, setLoading] = useState(false);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [antifraudCheck, setAntifraudCheck] = useState(false);
  const [userNewStatus, setUserNewStatus] = useState(null);
  const [userNewStatusReason, setUserNewStatusReason] = useState(null);
  const [forceStatus, setForceStatus] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [canForceStatus, setCanForceStatus] = useState(false);
  const [loadingStatusOptions, setLoadingStatusOptions] = useState(false);
  const [optionsError, setOptionsError] = useState(false);
  const [isStatusChangeModalVisible, setIsStatusChangeModalVisible] =
    useState(false);
  const [settleWalletLoadingId, setSettleWalletLoadingId] = useState(null);
  const [transferAllLoading, setTransferAllLoading] = useState(false);

  const isLimitedChargingMode =
    userDetails?.charging_settings?.charging_mode === ChargingMode.LIMITED;

  const { hasPermission } = useAccess();
  const canSettleNegativeBalance = hasPermission(
    "settle-user-negative-balance"
  );
  const canTransferNegativeFromOtherAccounts =
    hasPermission("can-transfer-balance") ||
    hasPermission("transfer-balance-cpo");

  useOnUpdate(() => {
    if (userDetails !== null) {
      setAntifraudCheck(!userDetails.skipAntifraudCheck);
    }
  }, [userDetails]);

  const fetchUserDetails = useCallback(async () => {
    const { data } = await api.get(`user/${userID}/address`);

    setUserDetails((currentUserDetails) => ({
      ...currentUserDetails,
      ...data,
    }));
  }, [userID]);

  const loadUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      await fetchUserDetails();
    } catch (error) {
      logHandledError("Failed to fetch user details", error);
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.ERROR_GET_DETAILS_USER),
      });
    } finally {
      setLoading(false);
    }
  }, [fetchUserDetails, freire]);

  const getUserDetails = async ([key]) => {
    if (userDetails === null && key === "personalData") {
      await loadUserDetails();
    }
  };

  const userTotalPendingBalance = useMemo(() => {
    if (!userDetails) {
      return 0;
    }

    if (typeof userDetails.userTotalPendingBalance === "number") {
      return userDetails.userTotalPendingBalance;
    }

    return (
      (userDetails.balance ?? 0) +
      (userDetails.paymentMethods?.reduce(
        (sum, paymentMethod) => sum + (paymentMethod?.balance ?? 0),
        0
      ) ?? 0)
    );
  }, [userDetails]);

  const toggleAntifraudeCheck = async (value) => {
    try {
      setLoadingToggle(true);

      await api.patch(`/users/${userDetails._id}/skip-antifraud-check`, {
        skipAntifraudCheck: !value,
      });

      setAntifraudCheck(value);

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.ANTIFRAUD_CHECK_CHANGED(value)),
      });
    } catch (error) {
      logHandledError("Failed to toggle antifraud check", error);
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.ANTIFRAUD_CHECK_CHANGED_ERROR),
      });
    } finally {
      setLoadingToggle(false);
    }
  };

  const loadStatusOptions = async () => {
    setLoadingStatusOptions(true);
    setOptionsError(false);
    setStatusOptions([]);
    setAllStatuses([]);
    setCanForceStatus(false);

    try {
      const { data } = await api.get(
        `/users/${userDetails._id}/status-options`
      );

      setStatusOptions(data?.options || []);
      setAllStatuses(data?.allStatuses || []);
      setCanForceStatus(Boolean(data?.canForce));
      setOptionsError(false);
    } catch (error) {
      logHandledError("Failed to load user status options", error);
      setStatusOptions([]);
      setAllStatuses([]);
      setCanForceStatus(false);
      setOptionsError(true);

      if (error.response?.status === 403) {
        const code = error.response?.data?.code;
        const translated = freire(texts.USER_STATUS_ERROR_BY_CODE)[code];

        notification.error({
          message: freire(texts.OOPS),
          description: translated || error.response?.data?.message,
        });
        return;
      }

      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.USER_STATUS_CHANGE_ERROR),
      });
    } finally {
      setLoadingStatusOptions(false);
    }
  };

  const saveNewUserStatus = async () => {
    if (forceStatus && (userNewStatusReason || "").length < 5) {
      message.error(freire(texts.FORCE_STATUS_REASON_REQUIRED));
      return;
    }

    try {
      setLoadingToggle(true);

      await api.put(`/users/${userDetails._id}/status`, {
        status: userNewStatus,
        status_reason: userNewStatusReason,
        force: forceStatus,
      });

      setUserDetails((currentUserDetails) => ({
        ...currentUserDetails,
        status: userNewStatus,
      }));

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.USER_STATUS_CHANGED),
      });

      setIsStatusChangeModalVisible(false);
      setUserNewStatus(null);
      setUserNewStatusReason(null);
      setForceStatus(false);
    } catch (error) {
      logHandledError("Failed to save user status", error);

      if (error.response?.status === 403) {
        const code = error.response?.data?.code;
        const translated = freire(texts.USER_STATUS_ERROR_BY_CODE)[code];

        notification.error({
          message: freire(texts.OOPS),
          description: translated || error.response?.data?.message,
        });
        return;
      }

      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.USER_STATUS_CHANGE_ERROR),
      });
    } finally {
      setLoadingToggle(false);
    }
  };

  const handleSettleNegativeWallet = async (walletPaymentMethod) => {
    if (!canSettleNegativeBalance) {
      message.error(freire(texts.SETTLE_NEGATIVE_BALANCE_ERROR));
      return;
    }

    const walletId = getWalletIdentifier(walletPaymentMethod);

    if (!walletId) {
      message.error(freire(texts.SETTLE_NEGATIVE_BALANCE_ERROR));
      return;
    }

    if (!canSettleViaPrimary) {
      message.error(freire(texts.SETTLE_NEGATIVE_BALANCE_ERROR));
      return;
    }

    setSettleWalletLoadingId(walletId);
    try {
      await api.post(
        `/admin/user/${userID}/payment-methods/wallet/${walletId}/settle-negative`
      );
      message.success(freire(texts.SETTLE_NEGATIVE_BALANCE_SUCCESS));
      await fetchUserDetails();
    } catch (error) {
      logHandledError("Failed to settle negative wallet", error);
      const responseMessage = error?.response?.data?.message;
      message.error({
        content: (
          <div>
            <div>{freire(texts.SETTLE_NEGATIVE_BALANCE_ERROR)}</div>
            {responseMessage ? (
              <div style={{ marginTop: 4 }}>{responseMessage}</div>
            ) : null}
          </div>
        ),
      });
    } finally {
      setSettleWalletLoadingId(null);
    }
  };

  const paymentMethods = userDetails?.paymentMethods;

  const primaryPaymentMethod = useMemo(
    () =>
      Array.isArray(paymentMethods) && paymentMethods.length > 0
        ? paymentMethods.find((paymentMethod) => paymentMethod?.primary) ??
          paymentMethods.find(
            (paymentMethod) =>
              getPaymentMethodBrand(paymentMethod).toLowerCase() !== "wallet"
          ) ??
          paymentMethods.find(isWallet)
        : null,
    [paymentMethods]
  );

  const primaryPaymentMethodBrand = getPaymentMethodBrand(primaryPaymentMethod);
  const normalizedPrimaryBrand = primaryPaymentMethodBrand.toLowerCase();
  const canSettleViaPrimary =
    Boolean(primaryPaymentMethod) && normalizedPrimaryBrand !== "wallet";

  const primaryPaymentMethodLabel = useMemo(
    () =>
      primaryPaymentMethod
        ? formatPaymentMethod({ freire, paymentMethod: primaryPaymentMethod })
        : null,
    [freire, primaryPaymentMethod]
  );

  const renderSettleConfirmContent = useCallback(
    () => (
      <div>
        <div>{freire(texts.SETTLE_NEGATIVE_BALANCE_CONFIRM)}</div>
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Icon
            type="info-circle"
            style={{ color: "#1890ff", marginRight: 4 }}
          />
          <span>
            {freire(texts.SETTLE_NEGATIVE_BALANCE_METHOD_INFO)} (
            {primaryPaymentMethodLabel ??
              freire(texts.SETTLE_NEGATIVE_BALANCE_METHOD_UNKNOWN)}
            )
          </span>
        </div>
      </div>
    ),
    [freire, primaryPaymentMethodLabel]
  );

  const renderNegativeBalanceTag = () => {
    const actions = [];

    const negativeWallets =
      userDetails?.paymentMethods?.filter((paymentMethod) => {
        if (!isWallet(paymentMethod)) {
          return false;
        }

        const balanceValue = getPaymentMethodBalance(paymentMethod);
        if (typeof balanceValue !== "number") {
          return false;
        }

        return balanceValue < 0;
      }) ?? [];

    if (canSettleNegativeBalance && canSettleViaPrimary) {
      negativeWallets.forEach((walletPaymentMethod) => {
        const walletId = getWalletIdentifier(walletPaymentMethod);
        if (!walletId) {
          return;
        }

        const isPrimaryCompatible = isPrimaryCompatibleWithWallet(
          primaryPaymentMethod,
          walletPaymentMethod
        );

        if (!isPrimaryCompatible) {
          actions.push(
            <Tooltip
              key={`settle-negative-${walletId}-incompatible`}
              placement="topRight"
              title={freire(texts.SETTLE_NEGATIVE_BALANCE_PRIMARY_INCOMPATIBLE)}
            >
              <Tag
                className="paymentStatusTag"
                style={{
                  marginLeft: actions.length > 0 ? 12 : 16,
                  cursor: "default",
                }}
              >
                <Icon
                  type="info"
                  style={{ color: "#909090", marginRight: -3, marginLeft: -3 }}
                />
              </Tag>
            </Tooltip>
          );
          return;
        }

        const isLoading = settleWalletLoadingId === walletId;

        const settleText = (
          <Text
            underline
            style={{
              marginLeft: actions.length > 0 ? 12 : 16,
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
              userSelect: "none",
              color: "rgb(89, 89, 89)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {freire(texts.SETTLE_NEGATIVE_BALANCE)}
          </Text>
        );

        if (isLoading) {
          actions.push(
            <span key={`settle-negative-${walletId}`}>{settleText}</span>
          );
        } else {
          actions.push(
            <Popconfirm
              key={`settle-negative-${walletId}`}
              title={renderSettleConfirmContent()}
              onConfirm={() => handleSettleNegativeWallet(walletPaymentMethod)}
              okText={freire(texts.YES)}
              cancelText={freire(texts.NO)}
              onCancel={(e) => e?.stopPropagation?.()}
            >
              {settleText}
            </Popconfirm>
          );
        }
      });
    } else if (
      canSettleNegativeBalance &&
      !canSettleViaPrimary &&
      negativeWallets.length > 0
    ) {
      actions.push(
        <Tooltip
          key="settle-negative-disabled"
          placement="topRight"
          title={freire(texts.SETTLE_NEGATIVE_BALANCE_PRIMARY_IS_WALLET)}
        >
          <Tag
            className="paymentStatusTag"
            style={{
              marginLeft: actions.length > 0 ? 12 : 16,
              cursor: "default",
            }}
          >
            <Icon
              type="info"
              style={{ color: "#909090", marginRight: -3, marginLeft: -3 }}
            />
          </Tag>
        </Tooltip>
      );
    }

    return actions.length > 0 ? actions : null;
  };

  const transferAllBalancesToCurrent = async () => {
    try {
      const others =
        userDetails?.antifraud?.pendingBalances?.filter(
          (balanceItem) =>
            balanceItem["user_id"] !== userDetails._id &&
            balanceItem["user_id"] !== undefined
        ) ?? [];

      if (others.length === 0) {
        return;
      }

      setTransferAllLoading(true);

      for (const item of others) {
        await api.post(`/admin/users/${item["user_id"]}/transfer_balance`, {
          targetUserId: userDetails._id,
        });
      }

      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.TRANSFER_BALANCE_SUCCESS),
      });
      await fetchUserDetails();
    } catch (error) {
      logHandledError("Failed to transfer balances to current user", error);
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.TRANSFER_BALANCE_ERROR),
      });
    } finally {
      setTransferAllLoading(false);
    }
  };

  // Move IS_PERSON_FRAUDSTER InfoRow value to its own function
  const renderIsPersonFraudster = () => {
    if (!userDetails?.antifraud?.isFraudster) {
      return freire(texts.NO);
    }

    const pending = userDetails?.antifraud?.pendingBalances ?? [];
    const others = pending.filter((b) => b["user_id"] !== userDetails._id);

    const totalOtherBalance = others.reduce(
      (sum, item) => sum + (item["balance"] ?? 0),
      0
    );
    const shouldShowAggregated = totalOtherBalance < 0;

    const aggregatedRow = shouldShowAggregated ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ color: "#f40000" }}>
          {freire(texts.PENDING_BALANCE_IN_OTHER_ACCOUNTS)}{" "}
          {formatters.currency(
            freire.userLanguage,
            userDetails.currency
          )(totalOtherBalance / 100)}
        </div>
        {others.length > 0 && canTransferNegativeFromOtherAccounts && (
          <Popconfirm
            title={freire(texts.TRANSFER_BALANCE_CONFIRM_NO_EMAIL)}
            onConfirm={transferAllBalancesToCurrent}
            okText={freire(texts.YES)}
            cancelText={freire(texts.NO)}
          >
            <Button
              type="link"
              style={{
                padding: 0,
                marginLeft: 4,
                opacity: transferAllLoading ? 0.6 : 1,
              }}
              disabled={transferAllLoading}
            >
              <Icon type="money-collect" /> {freire(texts.TRANSFER_BALANCE)}
            </Button>
          </Popconfirm>
        )}
      </div>
    ) : null;

    const domainStatusRow = (
      <div>
        {freire(texts.EMAIL)} (<i>{userDetails.email.replace(/^[^@]+@/, "")}</i>
        ):{" "}
        {
          freire(texts.EMAIL_DOMAIN_STATUS)[
            userDetails?.antifraud?.domainStatus
          ]
        }
      </div>
    );

    if (hasPermission("can-transfer-balance")) {
      // Keep detailed list with emails only for developer permission
      return [
        aggregatedRow,
        ...others
          .map((key) => (
            <div
              key={key["user_id"]}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Copy>{key["email"]}</Copy>{" "}
              <div style={{ color: "#f40000" }}>
                {formatters.currency(
                  freire.userLanguage,
                  userDetails.currency
                )(key["balance"] / 100)}
              </div>
            </div>
          ))
          .concat(domainStatusRow),
      ];
    }

    // Default: privacy-friendly aggregated info, no emails
    return [aggregatedRow, domainStatusRow];
  };

  useEffect(() => {
    if (opened && !userDetails) {
      loadUserDetails();
    }
  }, [loadUserDetails, opened, userDetails]);

  const hasAnyStatusPermission =
    hasPermission("developer") ||
    hasPermission("edit-limited-user-status") ||
    hasPermission("edit-restricted-user-status");

  return (
    <Collapse
      bordered={false}
      onChange={getUserDetails}
      expandIconPosition="right"
      style={{ marginBottom: 30 }}
      defaultActiveKey={opened ? ["personalData"] : []}
    >
      <Panel
        key="personalData" // README: do not remove
        id="panel"
        header={
          <span className="drawerInfoTitle">{freire(texts.PERSONAL_DATA)}</span>
        }
      >
        <DetailsPanelContent
          allStatuses={allStatuses}
          antifraudCheck={antifraudCheck}
          canForceStatus={canForceStatus}
          forceStatus={forceStatus}
          freire={freire}
          getWhitelabelByAlias={getWhitelabelByAlias}
          hasAnyStatusPermission={hasAnyStatusPermission}
          hasPermission={hasPermission}
          isLimitedChargingMode={isLimitedChargingMode}
          isStatusChangeModalVisible={isStatusChangeModalVisible}
          loadStatusOptions={loadStatusOptions}
          loading={loading}
          loadingStatusOptions={loadingStatusOptions}
          loadingToggle={loadingToggle}
          optionsError={optionsError}
          primaryPaymentMethod={primaryPaymentMethod}
          renderIsPersonFraudster={renderIsPersonFraudster}
          renderNegativeBalanceTag={renderNegativeBalanceTag}
          saveNewUserStatus={saveNewUserStatus}
          setForceStatus={setForceStatus}
          setIsStatusChangeModalVisible={setIsStatusChangeModalVisible}
          setUserNewStatus={setUserNewStatus}
          setUserNewStatusReason={setUserNewStatusReason}
          statusOptions={statusOptions}
          toggleAntifraudeCheck={toggleAntifraudeCheck}
          userDetails={userDetails}
          userNewStatus={userNewStatus}
          userNewStatusReason={userNewStatusReason}
          userTotalPendingBalance={userTotalPendingBalance}
        />
      </Panel>
    </Collapse>
  );
};

export { PAYMENT_METHOD_BRANDS as BRANDS };
export default Details;
